// requestFundingLegs.js
//
// A6 (2026-07-19) — the ONE client-side phrasing of a request's funding legs.
//
// INPUT is the server's `funding_legs` array from GET /requests/my — the SAME
// list the admin review renders (computeRequestFunding, A5), reduced to a
// client-safe subset. This module NEVER derives a leg the server did not send
// and does no arithmetic beyond formatting: the money story is server truth,
// re-phrased for the client.
//
// PHRASING RULES (money contract):
//   * Money that MOVED (timing 'captured') renders with its Dubai date and is
//     shown for EVERY request status — a withdrawn request's captured card
//     payment still happened and must stay visible.
//   * Money that is a PLAN (on_approval / on_completion / awaiting_capture)
//     renders only while the plan is live: pending requests, or — for the
//     wallet leg — an APPROVED request, where "on approval" has become "paid
//     when we approved it" (dated from reviewed_at). Rejected/cancelled
//     requests never show a plan leg: that money was never taken.
//   * A review-held card payment says "in review", never "not completed" —
//     the money moved and is with the bank (payment-outcome contract: no
//     not-charged claim without positive evidence). Same server field and
//     precedence as the mobile mirror: payment_state 'payment_in_review'
//     outranks the awaiting-capture "not completed" phrasing.
//   * One figure per leg, gross (deposit-headroom rule: no netted figures).
//   * Timestamps render Asia/Dubai via the shared formatter.

import { formatDubai } from '../../../utils/dubaiTime';
import { isReviewHeldRow } from '../paymentErrors';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dubaiDay = (v) => formatDubai(v, 'd MMM yyyy');

// Money that MOVED gets a time, not just a day (2026-07-23). A capture and its return can happen minutes apart, and a bare date
// makes them look like one event. Plan legs keep the day — there is no moment
// to state for money that hasn't moved.
const dubaiStamp = (v) => formatDubai(v, 'd MMM yyyy, HH:mm');

// Last 4 digits of a masked PAN ('4111********9999' → '9999').
const last4 = (masked) => {
    const m = String(masked || '').match(/(\d{4})\s*$/);
    return m ? m[1] : null;
};

/**
 * The client-phrased payment rows for one request.
 *
 * @param request  a row from GET /requests/my (status, reviewed_at,
 *                 funding_legs, funding_rail).
 * @returns [{ key, label, amount (string|null), sub (string|null) }]
 *          Empty array ⇒ render no payment section at all (no legs sent,
 *          read failed, or nothing truthful to show for this status).
 */
export function clientFundingRows(request) {
    const legs = Array.isArray(request?.funding_legs) ? request.funding_legs : [];
    if (legs.length === 0) return [];
    const status = String(request?.status || '');
    // 'awaiting_payment' is a LIVE plan too: the request is staged and its
    // card checkout is open — the client must see what the payment will be,
    // exactly as for a pending request.
    const isPending = status === 'pending' || status === 'awaiting_payment';
    const isApproved = status === 'approved';
    // The bank is holding an authorised payment (payment_state
    // 'payment_in_review') — the same server field the status chips read.
    const inReview = isReviewHeldRow(request);

    const rows = [];
    legs.forEach((leg, i) => {
        const key = `${leg.kind}-${i}`;
        if (leg.kind === 'card' && leg.timing === 'captured') {
            const four = last4(leg.card_masked);
            rows.push({
                key,
                label: four ? `Card •••• ${four}` : 'Card',
                amount: money(leg.amount),
                sub: leg.when ? `Paid ${dubaiStamp(leg.when)}` : 'Paid',
            });
            return;
        }
        // The return of a card payment (rejected request / manual credit). Its
        // own dated row next to the payment it reverses — shown for EVERY
        // status, because money coming back is as real as money going out.
        if (leg.kind === 'card_refund') {
            rows.push({
                key,
                label: 'Returned to your wallet',
                amount: `+${money(leg.amount)}`,
                sub: leg.when
                    ? `Credited ${dubaiStamp(leg.when)}`
                    : 'Credited to your wallet balance',
                credit: true,
            });
            return;
        }
        if (leg.kind === 'card' && leg.timing === 'awaiting_capture') {
            // Only actionable while pending; and NEVER "nothing was charged" —
            // an unfinished checkout is not proof the money stayed put. A
            // review-held payment DID move, so it must never read as "not
            // completed" (and never as "paid" either — the bank has it).
            if (!isPending) return;
            rows.push({
                key,
                label: 'Card',
                amount: money(leg.amount),
                sub: inReview
                    ? 'Payment in review — held by your bank'
                    : 'Payment not completed yet',
            });
            return;
        }
        if (leg.kind === 'bank_transfer') {
            rows.push({
                key,
                label: 'Bank transfer',
                amount: money(leg.amount),
                sub: leg.when ? `Received ${dubaiStamp(leg.when)}` : 'Received',
            });
            return;
        }
        if (leg.kind === 'wallet') {
            if (isApproved) {
                rows.push({
                    key,
                    label: 'Wallet',
                    amount: money(leg.amount),
                    sub: request.reviewed_at
                        ? `Paid from your wallet ${dubaiDay(request.reviewed_at)}`
                        : 'Paid from your wallet',
                });
            } else if (isPending) {
                rows.push({
                    key,
                    label: 'Wallet',
                    amount: money(leg.amount),
                    sub: 'Charged when your request is approved',
                });
            }
            // rejected/cancelled: the wallet was never charged — no row.
            return;
        }
        if (leg.kind === 'auto_charge') {
            if (!isPending && !isApproved) return;
            const four = last4(leg.card_masked);
            rows.push({
                key,
                label: four ? `Auto Payment · card •••• ${four}` : 'Auto Payment',
                amount: money(leg.amount),
                sub: 'Charged to your saved card when the service is completed',
            });
            return;
        }
        if (leg.kind === 'none') {
            if (!isPending && !isApproved) return;
            rows.push({ key, label: 'No payment needed', amount: null, sub: null });
            return;
        }
        if (leg.kind === 'recorded_before_rails') {
            // Honest placeholder, plainly worded — never a scare banner.
            if (!isPending && !isApproved) return;
            rows.push({
                key,
                label: 'Payment details not recorded for this older request',
                amount: null,
                sub: null,
            });
        }
    });
    return rows;
}

/**
 * ONE compact line for list rows: "Wallet AED 245.70" /
 * "Card •••• 9999 AED 145.70 + Wallet AED 100.00" /
 * "Auto Payment — charged at completion". '' ⇒ show nothing.
 */
export function clientFundingSummary(request) {
    const rows = clientFundingRows(request);
    if (rows.length === 0) return '';
    const parts = rows.map((r) => {
        if (r.amount == null) return r.label;
        if (String(r.label).startsWith('Auto Payment')) {
            return `${r.label} ${r.amount} — charged at completion`;
        }
        return `${r.label} ${r.amount}`;
    });
    return parts.join(' + ');
}
